import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import MagicButton from 'components/MagicButton/MagicButton';
import SmsConsentDisclosure from 'components/SmsConsentDisclosure/SmsConsentDisclosure';
import StatusMessage from 'components/StatusMessage/StatusMessage';
import { showSuccess } from 'utils/toastNotifications';
import {
  useSendVerificationCodeMutation,
  useVerifyPhoneMutation,
  useGetPhoneVerificationStatusQuery
} from 'store/services/auth/authApi';
import { loginSuccess } from 'store/services/auth/authSlice';
import { RootState } from 'store';
import { getPhoneVerificationErrorMessage } from 'utils/phoneVerificationErrors';
import './MobileVerificationModal.css';

/**
 * The phone-entry and code-entry steps of phone verification, extracted from
 * MobileVerificationModal so two shells can render them without forking the
 * logic: the modal itself (Settings, Home checklist, My Cards) and the
 * Manage SMS Alerts modal, which embeds them as steps 1 and 2 of its own flow.
 *
 * The CSS import lives here so the step styles travel with the steps — the
 * alerts modal renders `.code-input` et al. without importing the modal.
 */

/** Args handed to a host that renders its own footer chrome. */
export interface PhoneVerificationFooterState {
  /** Which step is rendering, so a host can supply its own labels. */
  step: 'phone' | 'verify';
  /** Default label: 'Send Code' on the phone step, 'Verify' on the code step. */
  primaryLabel: string;
  onPrimary: () => void;
  primaryDisabled: boolean;
  onCancel: () => void;
  cancelDisabled: boolean;
}

interface PhoneVerificationStepsProps {
  /**
   * False resets every field. Named for the fact that a host may keep this
   * mounted-but-inactive rather than unmounting it.
   */
  isActive: boolean;
  onCancel: () => void;
  /** Present when changing an existing number; switches the success copy. */
  currentPhone?: string | null;
  /** Receives the verified E.164 number so a host can display it immediately. */
  onVerificationSuccess?: (verifiedPhone?: string) => void;
  /** Optional heading above the phone step. Omitted by the modal shell. */
  heading?: React.ReactNode;
  /** Overrides the phone-step description. */
  description?: React.ReactNode;
  /**
   * When true (the default) the steps close themselves 2s after a successful
   * verify. Hosts that stay open on success — the alerts modal, which advances
   * to its own step 3 — pass false.
   */
  autoCloseOnSuccess?: boolean;
  onStepChange?: (step: 'phone' | 'verify') => void;
  /** Replaces the default two-button footer. */
  renderFooter?: (state: PhoneVerificationFooterState) => React.ReactNode;
}

const PhoneVerificationSteps: React.FC<PhoneVerificationStepsProps> = ({
  isActive,
  onCancel,
  currentPhone,
  onVerificationSuccess,
  heading,
  description,
  autoCloseOnSuccess = true,
  onStepChange,
  renderFooter
}) => {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);

  const [step, setStep] = useState<'phone' | 'verify'>('phone');
  const [phoneValue, setPhoneValue] = useState<string | undefined>('');
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [canResend, setCanResend] = useState(true);
  const [resendTimer, setResendTimer] = useState(0);
  const [verificationSid, setVerificationSid] = useState('');

  const codeInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // API hooks
  const [sendCode, { isLoading: isSending, error: sendError, reset: resetSendError }] = useSendVerificationCodeMutation();
  const [verifyPhone, { isLoading: isVerifying, error: verifyError, reset: resetVerifyError }] = useVerifyPhoneMutation();
  const { refetch: refetchPhoneStatus } = useGetPhoneVerificationStatusQuery();

  // Reset form when the steps go inactive
  useEffect(() => {
    if (!isActive) {
      setStep('phone');
      setPhoneValue('');
      setVerificationCode(['', '', '', '', '', '']);
      setCanResend(true);
      setResendTimer(0);
      setVerificationSid('');
      resetSendError();
      resetVerifyError();
    }
  }, [isActive, resetSendError, resetVerifyError]);

  // Keep a host's step indicator in sync, including the "Change phone number" path.
  useEffect(() => {
    onStepChange?.(step);
  }, [step, onStepChange]);

  // The success timer outlives a fast unmount (the unverified-phone banner hides
  // itself the moment the status refetch lands, mid-verify), so it must be cleared.
  useEffect(() => () => {
    if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
  }, []);

  // Handle resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  const isPhoneValid = () => {
    // PhoneInput returns E.164 format (e.g., "+14155551234")
    // A valid phone number should have at least country code + 7 digits
    return phoneValue && phoneValue.length >= 8; // +1 + at least 7 digits
  };

  const handleSendCode = async () => {
    if (!phoneValue) return;

    // Parse E.164 format phone number (e.g., "+14155551234")
    // Extract country code and phone number
    const matches = phoneValue.match(/^(\+\d{1,4})(\d+)$/);
    if (!matches) {
      console.error('Invalid phone number format');
      return;
    }

    const countryCode = matches[1]; // e.g., "+1"
    const digitsOnly = matches[2];  // e.g., "4155551234"

    try {
      const result = await sendCode({
        phoneNumber: digitsOnly,
        countryCode: countryCode
      }).unwrap();

      setVerificationSid(result.verificationSid);
      setStep('verify');
      setCanResend(false);
      setResendTimer(60); // 60 second cooldown

      // Focus first code input
      setTimeout(() => {
        codeInputRefs.current[0]?.focus();
      }, 100);
    } catch (err: any) {
      console.error('Failed to send verification code:', err);
    }
  };

  const handleResendCode = async () => {
    if (!canResend) return;

    // A failed Resend renders its own error below the button. Drop any stale
    // verify error first so only the most recent error is on screen.
    resetVerifyError();

    // Clear the verification code inputs
    setVerificationCode(['', '', '', '', '', '']);

    // Reset focus to first input
    codeInputRefs.current[0]?.focus();

    // Send new code
    await handleSendCode();
  };

  const handleCodeChange = (index: number, value: string) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(0, 1);

    // Typing a code means the user has moved on from a failed Resend.
    if (sendError) resetSendError();

    const newCode = [...verificationCode];
    newCode[index] = digit;
    setVerificationCode(newCode);

    // Auto-focus next input
    if (digit && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (sendError) resetSendError();
    const newCode = [...verificationCode];

    for (let i = 0; i < pastedText.length && i < 6; i++) {
      newCode[i] = pastedText[i];
    }

    setVerificationCode(newCode);

    // Focus the next empty input or the last one
    const nextEmptyIndex = newCode.findIndex(digit => !digit);
    if (nextEmptyIndex !== -1) {
      codeInputRefs.current[nextEmptyIndex]?.focus();
    } else {
      codeInputRefs.current[5]?.focus();
    }
  };

  const isCodeComplete = () => {
    return verificationCode.every(digit => digit !== '');
  };

  const handleVerifyCode = async () => {
    const code = verificationCode.join('');
    if (code.length !== 6 || !phoneValue) return;

    // Parse E.164 format to extract digits only
    const matches = phoneValue.match(/^(\+\d{1,4})(\d+)$/);
    if (!matches) {
      console.error('Invalid phone number format');
      return;
    }
    const digitsOnly = matches[2];

    try {
      const result = await verifyPhone({
        phoneNumber: digitsOnly, // Must match what was sent to send-verification-code
        code,
        verificationSid
      }).unwrap();

      // Update tokens using secure auth flow
      if (result.token || result.csrfToken) {
        dispatch(loginSuccess({
          token: result.token || '',
          csrfToken: result.csrfToken,
          user: user || undefined // Use existing user from Redux state
        }));
      }

      // Show success message
      showSuccess(currentPhone ? 'Phone number updated successfully!' : 'Phone verified successfully!');

      // Refresh phone status
      await refetchPhoneStatus();

      // Call success callback
      if (onVerificationSuccess) {
        onVerificationSuccess(phoneValue);
      }

      // Close after a short delay — unless the host stays open to show its own
      // next step.
      if (autoCloseOnSuccess) {
        autoCloseTimerRef.current = setTimeout(() => {
          onCancel();
        }, 2000);
      }
    } catch (err: any) {
      console.error('Failed to verify code:', err);
    }
  };

  const handleBackToPhone = () => {
    setStep('phone');
    setVerificationCode(['', '', '', '', '', '']);
    resetVerifyError();
  };

  const footerState: PhoneVerificationFooterState =
    step === 'phone'
      ? {
          step: 'phone',
          primaryLabel: 'Send Code',
          onPrimary: handleSendCode,
          primaryDisabled: !isPhoneValid() || isSending,
          onCancel,
          cancelDisabled: isSending
        }
      : {
          step: 'verify',
          primaryLabel: 'Verify',
          onPrimary: handleVerifyCode,
          primaryDisabled: !isCodeComplete() || isVerifying,
          onCancel,
          cancelDisabled: isVerifying
        };

  const footer = renderFooter ? (
    renderFooter(footerState)
  ) : (
    <div className="mobile-verification-modal-footer">
      <MagicButton
        label="Cancel"
        color="dark-grey"
        onPress={footerState.onCancel}
        isDisabled={footerState.cancelDisabled}
      />
      <MagicButton
        label={footerState.primaryLabel}
        color="purple"
        onPress={footerState.onPrimary}
        isDisabled={footerState.primaryDisabled}
      />
    </div>
  );

  if (step === 'phone') {
    return (
      <>
        {heading && <p className="verification-code-label">{heading}</p>}

        <p className="mobile-verification-description">
          {description ?? (currentPhone
            ? 'Enter a new phone number to update your account.'
            : 'Verify to unlock all features and claim your first 100 bonus points.')}
        </p>

        <div className="phone-input-wrapper">
          <PhoneInput
            international
            defaultCountry="US"
            value={phoneValue}
            onChange={setPhoneValue}
            className="react-phone-input"
            placeholder="Enter phone number"
          />
        </div>

        {sendError && (
          <StatusMessage
            type="error"
            message={getPhoneVerificationErrorMessage(sendError, 'send')}
          />
        )}

        <SmsConsentDisclosure />

        {footer}
      </>
    );
  }

  return (
    <>
      <div className="verification-code-section">
        <p className="verification-code-label">Enter the verification code</p>
        <p className="verification-code-sublabel">
          We sent a 6-digit code to {phoneValue}
        </p>

        <div className="verification-code-inputs">
          {verificationCode.map((digit, index) => (
            <input
              key={index}
              ref={(el) => {
                codeInputRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              className="code-input"
              value={digit}
              onChange={(e) => handleCodeChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined}
              maxLength={1}
              autoFocus={index === 0}
            />
          ))}
        </div>

        {resendTimer > 0 && (
          <p className="resend-timer">
            Resend code in {resendTimer} seconds
          </p>
        )}

        {canResend && (
          <button
            className="resend-button"
            onClick={handleResendCode}
            disabled={isSending}
          >
            Resend code
          </button>
        )}

        {/* Resend uses the send endpoint, so its failures (rate limit,
            unsupported country, temporarily unavailable) surface here.
            Without this a failed Resend renders nothing. */}
        {sendError ? (
          <StatusMessage
            type="error"
            message={getPhoneVerificationErrorMessage(sendError, 'send')}
          />
        ) : null}

        <button
          className="back-to-phone-button"
          onClick={handleBackToPhone}
        >
          Change phone number
        </button>
      </div>

      {verifyError && (
        <StatusMessage
          type="error"
          message={getPhoneVerificationErrorMessage(verifyError, 'verify')}
        />
      )}

      {footer}
    </>
  );
};

export default PhoneVerificationSteps;
